import { useState } from 'react';
import { has } from 'lodash';
import PropTypes from 'prop-types';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import useRoles from 'common/hooks/useRoles';

import {
  ESP_POM_STALE_CLAIM_MINUTES,
  NON_ROUTABLE_ACTION,
  NON_ROUTABLE_QUEUE_STATUS,
  NON_ROUTABLE_SOURCE,
  NON_ROUTABLE_TERMINAL_STATUSES,
  RELAY_FAX_STALE_CLAIM_MINUTES,
} from '../constants/nonRoutableRequests';
import BringBackNonRoutableRequestDialog from './BringBackNonRoutableRequestDialog';
import CancelNonRoutableRequestDialog from './CancelNonRoutableRequestDialog';
import CancelRelayFaxDialog from './CancelRelayFaxDialog';
import MoveRelayFaxToPomDialog from './MoveRelayFaxToPomDialog';
import NonRoutableRelayFaxDetailDialog from './NonRoutableRelayFaxDetailDialog';
import ReconcileRelayFaxDialog from './ReconcileRelayFaxDialog';
import ResendRelayFaxDialog from './ResendRelayFaxDialog';
import RetryNonRoutableRequestRoutingDialog from './RetryNonRoutableRequestRoutingDialog';

/**
 * Each action the SERVER may offer, with the menu label and the dialog that carries it out.
 * Title Case throughout — sentence case beside "Bring Back to POM" read as a different kind of
 * control.
 */
const ACTION_MENU = {
  [NON_ROUTABLE_ACTION.RETRY_ROUTING]: {
    label: 'Retry Routing',
    Dialog: RetryNonRoutableRequestRoutingDialog,
  },
  [NON_ROUTABLE_ACTION.BRING_BACK]: {
    label: 'Bring Back to POM',
    Dialog: BringBackNonRoutableRequestDialog,
  },
  [NON_ROUTABLE_ACTION.CANCEL]: { label: 'Cancel Request', Dialog: CancelNonRoutableRequestDialog },
  [NON_ROUTABLE_ACTION.RESEND_FAX]: { label: 'Resend Fax', Dialog: ResendRelayFaxDialog },
  [NON_ROUTABLE_ACTION.RECONCILE_FAX]: { label: 'Reconcile Fax', Dialog: ReconcileRelayFaxDialog },
  [NON_ROUTABLE_ACTION.CANCEL_FAX]: { label: 'Cancel Fax Order', Dialog: CancelRelayFaxDialog },
  [NON_ROUTABLE_ACTION.MOVE_TO_POM]: { label: 'Move to POM', Dialog: MoveRelayFaxToPomDialog },
};

// Read-only, so it is not one of the server's actions: any parked fax can be looked at.
const VIEW_FAX_DETAILS = 'VIEW_FAX_DETAILS';

/**
 * The actions to render, in the SERVER'S order. A value this build does not know is skipped, never
 * rendered and never a crash: the enum grows with the backend, and an unlabelled item with no dialog
 * behind it would be a button that does nothing.
 */
const offeredActions = (availableActions) =>
  (availableActions ?? []).filter((action) => has(ACTION_MENU, action));

/**
 * Why the row offers no action, in the operator's terms. Shown as the tooltip on the menu button
 * whenever the row offers no ACTION — including a parked fax whose menu still opens to its detail.
 *
 * It explains; it does not decide. Whether a row is actionable is the server's `availableActions`.
 *
 * A cancelled request is the opposite of an in-flight one — nothing is pending and nobody needs to
 * be called — so it gets its own message rather than an escalation. An in-flight row is told apart
 * by source, because each source's in-flight claim ends differently: a fax resend waits on the fax
 * service, an ESP / POM retry runs inside the request that made it, an intake retry or release is
 * mid-publish.
 */
const resolveDisabledReason = ({ canView, item }) => {
  if (!canView) {
    return 'Only a system administrator can act on a parked request';
  }

  if (NON_ROUTABLE_TERMINAL_STATUSES.has(item.status)) {
    // An ESP / POM order was placed in ESP or POM, not sent by a partner over the API.
    return item.source === NON_ROUTABLE_SOURCE.ESP_POM
      ? 'This request is closed. A cancelled request cannot be reopened — the order must be placed again in ESP or POM.'
      : 'This request is closed. A cancelled request cannot be reopened — the partner must send it again.';
  }

  if (item.status === NON_ROUTABLE_QUEUE_STATUS.RESOLUTION_IN_PROGRESS) {
    if (item.source === NON_ROUTABLE_SOURCE.RELAY_FAX) {
      return `A resend of this fax is in progress. If no result comes back, Reconcile and Cancel Fax become available here about ${RELAY_FAX_STALE_CLAIM_MINUTES} minutes after the last attempt.`;
    }
    if (item.source === NON_ROUTABLE_SOURCE.ESP_POM) {
      return `A retry of this order is running. If it does not finish, Retry becomes available here again ${ESP_POM_STALE_CLAIM_MINUTES} minutes after it started.`;
    }
    return 'Only a Pending request can be acted on. This one is already being resolved — check with engineering before touching it.';
  }

  return 'No action is available on this request right now. Refresh the queue to see its latest state.';
};

/**
 * Row menu for the Non-Routable Orders holding queue (POM-6223, POM-6279).
 *
 * IT RENDERS `availableActions` AND NEVER WORKS THEM OUT. The server decides, per row, from source,
 * reason, status, fax code, stale claim and whether relay orders may exist
 * (smartphos-core nonRoutableActionPolicy.ts), and refuses anything else with
 * NON_ROUTABLE_REQUEST_ACTION_NOT_ALLOWED. Gating on status here (as this menu once did) offered
 * Bring Back on rows the server refuses it for and hid the stale-claim escapes it allows.
 *
 * By source: API intake — Retry Routing, Bring Back, Cancel. Relay fax — Resend, Reconcile, Move
 * to POM, Cancel fax, plus a read-only fax detail view. ESP / POM — Retry (synchronous) and Cancel, never Bring
 * Back. A stale in-flight claim re-opens per source (fax: Reconcile / Cancel fax; ESP / POM: Retry).
 *
 * The dialogs stay mounted with the row and reset themselves on open; see useNonRoutableResolution.
 */
const NonRoutableRequestsQueueOptions = ({ item }) => {
  const { viewNonRoutableOrders } = useRoles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const menuOpen = !!anchorEl;

  const actions = viewNonRoutableOrders ? offeredActions(item.availableActions) : [];
  const canViewFax = viewNonRoutableOrders && item.source === NON_ROUTABLE_SOURCE.RELAY_FAX;
  const hasMenu = actions.length > 0 || canViewFax;

  const disabledReason =
    actions.length > 0 ? '' : resolveDisabledReason({ canView: viewNonRoutableOrders, item });

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const openFor = (key) => () => {
    handleMenuClose();
    setOpenDialog(key);
  };
  const closeDialog = () => setOpenDialog(null);

  return (
    <>
      <Tooltip title={disabledReason} arrow>
        {/* A disabled button emits no pointer events; the span carries the tooltip for it. */}
        <span data-testid={`WorkQueue-NonRoutable-${item.nonRoutableRequestId}-options`}>
          <IconButton
            id={`WorkQueue-NonRoutable-${item.nonRoutableRequestId}-options-button`}
            aria-label={`Non-routable request options ${item.referenceId}`}
            aria-controls={
              menuOpen
                ? `WorkQueue-NonRoutable-${item.nonRoutableRequestId}-options-menu`
                : undefined
            }
            aria-haspopup='true'
            aria-expanded={menuOpen ? 'true' : undefined}
            disabled={!hasMenu}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        id={`WorkQueue-NonRoutable-${item.nonRoutableRequestId}-options-menu`}
        aria-labelledby={`WorkQueue-NonRoutable-${item.nonRoutableRequestId}-options-button`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {canViewFax && <MenuItem onClick={openFor(VIEW_FAX_DETAILS)}>View Fax Details</MenuItem>}
        {actions.map((action) => (
          <MenuItem key={action} onClick={openFor(action)}>
            {ACTION_MENU[action].label}
          </MenuItem>
        ))}
      </Menu>

      {canViewFax && (
        <NonRoutableRelayFaxDetailDialog
          open={openDialog === VIEW_FAX_DETAILS}
          onClose={closeDialog}
          item={item}
        />
      )}
      {actions.map((action) => {
        const { Dialog } = ACTION_MENU[action];
        return (
          <Dialog key={action} open={openDialog === action} onClose={closeDialog} item={item} />
        );
      })}
    </>
  );
};

NonRoutableRequestsQueueOptions.propTypes = {
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    status: PropTypes.string.isRequired,
    source: PropTypes.string,
    availableActions: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default NonRoutableRequestsQueueOptions;
